/**
 * Integrated FetchManager with RateLimiter
 * COMPLETELY BYPASSES @solana/web3.js Connection retry logic
 * Uses only custom fetch to prevent retry loops
 */

import { RateLimiter } from "./../rateLimiter/index.js";
import type { Umi } from "@metaplex-foundation/umi";
import { publicKey } from "@metaplex-foundation/umi";
import { 
  fetchDigitalAsset,
  mplTokenMetadata,
  createSignerFromKeypair,
  generateSigner,
  signerIdentity,
  PublicKey,
  getLogString,
  convertBigInts
} from "./../imports/index.js";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";


// Singleton instances
export const rateLimiter = RateLimiter.getInstance();
export function getRpcUrl(method: any = null) {
  return rateLimiter.getUrl(method);
}
export function logResponse(method: any, result: any, url: any) {
  rateLimiter.logResponse(method, result, url);
}
export function getFallback() {
  return rateLimiter.getFallbackUrl()

}

// Registry for inflight requests (queue pattern)
const inflightRegistry = new Map<string, Promise<any>>();

/**
 * Registry for Umi instances
 * NO CONNECTION OBJECTS - uses only custom fetch
 */

/**
 * Registry for Umi instances
 */
class UmiRegistry {
  private cache = new Map<string, Umi>();
  private rateLimiter: RateLimiter;

  constructor(rateLimiter: RateLimiter) {
    this.rateLimiter = rateLimiter;
  }
  getUmi(url:any=null,method:any=null) {
    method = method || "umi_init"
    url = url ||  this.rateLimiter.getUrl(method);

    const umi = createUmi(
      url as any,
      ({ customFetch: createRateLimitedFetch() } as any)
    );

    umi.use(mplTokenMetadata());
    umi.use(
      signerIdentity(
        createSignerFromKeypair(umi, generateSigner(umi))
      )
    );

    return umi;
  }
}

function createRateLimitedFetch() {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = extractMethodFromRequest(init);
    const url = getRpcUrl(method);

    const res = await fetch(url as string, init);
    logResponse(method, res.clone(), url);

    if (res.status === 429) {
      const fallbackUrl = getFallback() 
      const fallbackRes = await fetch(fallbackUrl, init);
      logResponse(method, fallbackRes.clone(), fallbackUrl);
      return fallbackRes;
    }

    return res;
  };
}

function extractMethodFromRequest(init?: RequestInit): string {
  if (!init?.body) return "default";
  try {
    const body = JSON.parse(init.body as string);
    return body.method ?? "default";
  } catch {
    return "default";
  }
}



/**
 * FetchManager with integrated RateLimiter
 * NO Connection objects to avoid retry loops
 */
export class FetchManager {
  private rateLimiter: RateLimiter;
  private umiRegistry: UmiRegistry;
  private inflightRegistry: Map<string, Promise<any>>;

  constructor(
    urls: string[],
    fallbackUrl: string,
    rateLimiter?: any,
    umiRegistry?: any
  ) {
    //console.log('[FetchManager] Initializing with rate limiting');
    //console.log(`[FetchManager] Primary URLs: ${urls.join(', ')}`);
    //console.log(`[FetchManager] Fallback (unlimited): ${fallbackUrl}`);
    
    // Explicit wiring - no Connection objects
    rateLimiter = rateLimiter || RateLimiter;
    this.rateLimiter = rateLimiter(urls, fallbackUrl);
    umiRegistry = umiRegistry || UmiRegistry
    this.umiRegistry = umiRegistry(this.rateLimiter)
    this.inflightRegistry = inflightRegistry;
    
    //console.log('[FetchManager] Initialized - all fetches will use rate limiter');
  }

  /**
   * Swap URLs at runtime
   */
  setUrls(urls: string[], fallbackUrl: string): void {
    //console.log('[FetchManager] Updating URLs');
    this.rateLimiter = new RateLimiter(urls, fallbackUrl);
    this.umiRegistry = new UmiRegistry(this.rateLimiter);
    
  }

  /**
   * Generic RPC fetch with rate limiting
   */
  async fetchRpc<T = any>(method: string, params: any[]): Promise<T> {
    const inflightKey = `${method}:${JSON.stringify(params)}`;
    
    if (this.inflightRegistry.has(inflightKey)) {
      //console.log(`[FetchManager] Using inflight request for ${method}`);
      return this.inflightRegistry.get(inflightKey)!;
    }

    const promise = this._fetchRpcImpl(method, params);
    this.inflightRegistry.set(inflightKey, promise);

    try {
      return await promise;
    } finally {
      this.inflightRegistry.delete(inflightKey);
    }
  }

  /**
   * Internal RPC fetch implementation
   */
  private async _fetchRpcImpl<T = any>(method: string, params: any[]): Promise<T> {
    //console.log(`[FetchManager] fetchRpc: ${method}`);
    
    // Get rate-limited URL (waits for cooldown automatically)
    const url = await this.rateLimiter.getUrl(method);
    
    if (!url) {
      getLogString({logType:'error',function_name:"fetchManager",message:'No URL available from rate limiter',details: url});
      throw new Error('No URL available from rate limiter');
    }

    //console.log(`[FetchManager] Using URL: ${url}`);

    // Make request
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method,
        params,
      }),
    });

    //console.log(`[FetchManager] Response status: ${res.status}`);

    // Log response for rate limiting
    this.rateLimiter.logResponse(method, res.clone(), url);

    // Handle 429
    if (res.status === 429) {
      //console.log('[FetchManager] 429 detected, using unlimited fallback');
      
      const fallbackUrl = this.rateLimiter.getFallbackUrl();
      //console.log(`[FetchManager] Fallback URL: ${fallbackUrl}`);

      // Retry with unlimited fallback
      const fallbackRes = await fetch(fallbackUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method,
          params,
        }),
      });

      //console.log(`[FetchManager] Fallback response status: ${fallbackRes.status}`);
      this.rateLimiter.logResponse(method, fallbackRes.clone(), fallbackUrl);
      
      return fallbackRes.json();
    }

    return res.json();
  }

  /**
   * Get Umi instance
   * Uses rate-limited fetch internally (NO Connection object)
   */
  getUmi(url?: string): Umi {
    // If no URL provided, get from rate limiter
    const targetUrl = url || this.rateLimiter.getUrl('getUmi');
    
    if (!targetUrl) {
      getLogString({logType:'error',function_name:"fetchManager",message:` 'No URL available for Umi'`,details: url});
      throw new Error('No URL available for Umi');
    }

    //console.log(`[FetchManager] Getting Umi for URL: ${targetUrl}`);
    return this.umiRegistry.getUmi(targetUrl);
  }

  /**
   * Fetch Umi instance asynchronously
   */
  async fetchUmi(mint:any=null): Promise<any> {
    const method = 'getDigitalAsset';
    
    // Get rate-limited URL
    const url = this.rateLimiter.getUrl(method);
    
    if (!url) {
      throw new Error('No URL available from rate limiter');
    }
    let status=400
    const umi = this.getUmi(url);
    if (!umi){
      status=400

        
    }
    const fakeResponse = new Response(convertBigInts(umi), {
        status,
        headers: { 'Content-Type': 'application/json' }
      });
    //console.log(`[FetchManager] Fetching Umi for URL: ${url}`);
    this.rateLimiter.logResponse(method, fakeResponse, url);
    
    
    
    return umi
  }

  /**
   * Fetch metadata with rate limiting and deduplication
   */
  async fetchMetaData(mint: PublicKey): Promise<any> {
    const mintStr = mint.toString();
    
    // Check inflight (queue pattern)
    if (this.inflightRegistry.has(mintStr)) {
      //console.log(`[FetchManager] Using inflight metadata request for ${mintStr}`);
      return this.inflightRegistry.get(mintStr)!;
    }

    // Create promise
    const promise = this._fetchMetaDataImpl(mint);
    
    // Register inflight
    this.inflightRegistry.set(mintStr, promise);

    try {
      return await promise;
    } finally {
      // Clear inflight
      this.inflightRegistry.delete(mintStr);
    }
  }

  /**
   * Internal metadata fetch implementation
   */
  private async _fetchMetaDataImpl(mint: any): Promise<any> {
    const method = 'getDigitalAsset';
    
    ////console.log(`[FetchManager] fetchMetaData for ${mint.toString()}`);
    
    // Get URL from rate limiter (waits for cooldown)
    const url = this.rateLimiter.getUrl(method);
    
    if (!url) {
      throw new Error('No URL available from rate limiter');
    }

    ////console.log(`[FetchManager] Using URL for metadata: ${url}`);

    try {
      // Get Umi for this URL (uses rate-limited fetch)
      const umi = this.getUmi(url);
      
      ////console.log(`[FetchManager] Calling fetchDigitalAsset...`);
      
      // Fetch metadata
      const result = await fetchDigitalAsset(umi, mint);
      
      ////console.log(`[FetchManager] Successfully fetched metadata for ${mint.toString()}`);
      
      // Log succe ssful request
     const fakeResponse = new Response(convertBigInts(result), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
      this.rateLimiter.logResponse(method, fakeResponse, url);
      
      return result;
    } catch (error: any) {
      getLogString({logType:'error',function_name:"fetchManager",message:`Error fetching metadata:`,details: error.message});
      
      // Handle rate limiting errors
      if (error.message?.includes('429') || error.message?.includes('rate limit')) {
        //console.log('[FetchManager] Metadata fetch rate limited, using unlimited fallback');
        
        // Get unlimited fallback URL
        const fallbackUrl = this.rateLimiter.getFallbackUrl();
        ////console.log(`[FetchManager] Fallback URL: ${fallbackUrl}`);

        // Retry with fallback
        const fallbackUmi = this.getUmi(fallbackUrl);
        const result = await fetchDigitalAsset(fallbackUmi, publicKey(mint.toString()));
        
        //console.log(`[FetchManager] Successfully fetched metadata using fallback`);
        return result;
      }
      
      throw error;
    }
  }

  /**
   * Batch fetch metadata with rate limiting
   */
  async fetchMetaDataBatch(mints: PublicKey[]): Promise<any[]> {
    //console.log(`[FetchManager] Batch fetching ${mints.length} metadata`);
    
    const results: any[] = [];
    
    for (const mint of mints) {
      try {
        const result = await this.fetchMetaData(mint);
        results.push(result);
      } catch (error:any) {
        getLogString({logType:'error',message:`Failed to fetch metadata for ${mint.toString()}:`, details:error.message});
        results.push(null);
      }
    }
    
    //console.log(`[FetchManager] Batch fetch complete: ${results.filter(r => r !== null).length}/${mints.length} successful`);
    
    return results;
  }
  async getUrl(method:any=null){
    const url = await this.rateLimiter.getUrl(method);
    return url;
  }
  async logResponse(method:any=null, result:any=null, url:any=null){
    return await this.rateLimiter.logResponse(method, result, url);
  }
  async getFallbackUrl(method:any=null, result:any=null, url:any=null){
    return await this.rateLimiter.getFallbackUrl()
  }
  /**
   * Clear all caches
   */
  clearCaches(): void {
    //console.log('[FetchManager] Clearing all caches');
    
    this.inflightRegistry.clear();
  }
}